import { ReactEventHandler } from 'react';

import { useTooltipReducer } from '../../../../core/components/overlay-tooltip/useTooltipReducer.js';
import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { ZOOM_CHANGE_TYPE } from '../../../../core/types/zoom.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { reduceResetInteractions } from '../../../store/reducer-reset.js';
import { useSetState } from '../../../store/selectors.js';
import { isZoomEnabledFor } from '../../../utils/axis-interactions.js';
import { getInitialScale } from '../../../utils/get-scale.js';
import { handleZoomChange } from '../utils/zoom-change-handler.js';

export const useResetInteractions = () => {
  const dispatch = useTooltipReducer();
  const setState = useSetState();
  const { axes } = useXYChartData();
  const axisIdToScales = useXYChartScales();
  const { resetSelectionState } = useSynchronizedSelection();

  return (
    event?: Partial<ReactEventHandler> & {
      position?: RelativeAbsolutePosition;
    },
  ) => {
    for (const axis of axes) {
      if (isZoomEnabledFor(axis)) {
        const initialScale = getInitialScale(axisIdToScales, axis.id);
        const initialDomain = initialScale.domain() as number[] | Date[];

        handleZoomChange(
          axis,
          initialDomain[0],
          initialDomain[1],
          ZOOM_CHANGE_TYPE.Reset,
        );
      }
    }

    const allZoomablesAxesHaveInfiniteZoom =
      axes.filter(isZoomEnabledFor).length > 0 &&
      axes.filter(isZoomEnabledFor).every((axis) => axis.interactions.infinite);

    resetSelectionState?.();
    dispatch({ type: 'RESET_TOOLTIP' })();
    setState(
      reduceResetInteractions({
        position: event?.position,
        zoomStatus: {
          zoomOutEnabled: allZoomablesAxesHaveInfiniteZoom,
        },
      }),
    );
  };
};
